import React from 'react';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import { GameProvider } from '../../../types';
import { useDeleteGameProvider } from '../../../hooks/useApi';

interface DeleteGameProviderModalProps {
  provider: GameProvider | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const DeleteGameProviderModal: React.FC<DeleteGameProviderModalProps> = ({ provider, isOpen, onClose, onSuccess }) => {
  const deleteProvider = useDeleteGameProvider();

  if (!isOpen || !provider) return null;

  const handleDelete = async () => {
    try {
      await deleteProvider.mutateAsync(provider.id);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Delete provider error:', error);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black bg-opacity-50 overflow-y-auto overscroll-contain !m-0 !mt-0 p-4 sm:p-6"
      onClick={onClose}
    >
      <div className="min-h-full flex items-start justify-center">
        <Card className="w-full max-w-md my-4 sm:my-8" onClick={(e) => e.stopPropagation()}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg font-semibold">Delete Game Provider</CardTitle>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
              type="button"
            >
              <X className="h-5 w-5" />
            </button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start space-x-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800">
                  Are you sure you want to delete this game provider? This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Code:</span>
                <span className="font-medium font-mono">{provider.code}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Name:</span>
                <span className="font-medium">{provider.name}</span>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={deleteProvider.isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleteProvider.isLoading}
                className="flex items-center space-x-2 bg-red-600 hover:bg-red-700"
              >
                {deleteProvider.isLoading ? (
                  <>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    <span>Delete Provider</span>
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DeleteGameProviderModal;

